/**
 * Map a notification to the in-app page the user should open.
 * Used by the Layout bell dropdown and the Notifications page.
 */
export function getNotificationHref(notification) {
  const type = String(notification?.type || '').toLowerCase();
  switch (type) {
    case 'booking':
    case 'payment':
    case 'cancellation':
      return '/my-bookings';
    case 'open_play':
    case 'invitation':
      return '/open-play';
    case 'tournament_payment': {
      const tid = notification?.tournament_id;
      const pid = notification?.participant_id;
      if (!tid) return '/events';
      const qs = new URLSearchParams({
        highlight: tid,
        payment: 'return',
        tournament_id: tid,
      });
      if (pid) qs.set('participant_id', pid);
      return `/events?${qs.toString()}`;
    }
    case 'tournament_payment_incomplete': {
      const tid = notification?.tournament_id;
      const pid = notification?.participant_id;
      if (!tid) return '/events';
      const qs = new URLSearchParams({ highlight: tid });
      if (pid) qs.set('participant_id', pid);
      return `/events?${qs.toString()}`;
    }
    case 'tournament_refund':
    case 'tournament_concluded':
      return '/notifications';
    case 'tournament_restored': {
      const tid = notification?.tournament_id;
      return tid ? `/events?highlight=${encodeURIComponent(tid)}` : '/events';
    }
    case 'event': {
      const tid = notification?.tournament_id;
      return tid ? `/events?highlight=${encodeURIComponent(tid)}` : '/events';
    }
    case 'tournament_bracket':
    case 'tournament_withdrawal':
    case 'tournament_partner_change': {
      const tid = notification?.tournament_id;
      if (!tid) return '/events?tab=registered';
      const qs = new URLSearchParams({ tab: 'registered', highlight: tid });
      if (notification?.participant_id) qs.set('participant_id', notification.participant_id);
      return `/events?${qs.toString()}`;
    }
    case 'tournament_transfer': {
      const stage = String(notification?.stage || '').toLowerCase();
      const tid = stage === 'accepted'
        ? (notification?.target_tournament_id || notification?.tournament_id)
        : notification?.tournament_id;
      if (!tid) return '/events?tab=registered';
      const qs = new URLSearchParams({ tab: 'registered', highlight: tid });
      if (notification?.participant_id) qs.set('participant_id', notification.participant_id);
      return `/events?${qs.toString()}`;
    }
    case 'reward':
      return '/rewards';
    case 'membership':
      return '/memberships';
    case 'skill_level':
      return '/profile';
    case 'welcome':
      return '/dashboard';
    default:
      return '/notifications';
  }
}

const EVENTS_PANEL_TYPES = new Set([
  'tournament_payment',
  'tournament_payment_incomplete',
  'tournament_bracket',
  'tournament_withdrawal',
  'tournament_transfer',
]);

/** Unread paid / incomplete tournament notifications for the public Events panel badge. */
export function isUnreadTournamentPaymentNotification(n) {
  return Boolean(n)
    && EVENTS_PANEL_TYPES.has(String(n.type || '').toLowerCase())
    && !n.read;
}

export function formatNotificationAmount(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n) || n < 0) return null;
  return `₱${n.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Short CTA line for Events panel / notifications list. */
export function getTournamentNotificationCta(notification) {
  const type = String(notification?.type || '').toLowerCase();
  if (type === 'tournament_payment') {
    const amt = formatNotificationAmount(notification?.amount);
    return amt ? `Paid ${amt} · View receipt` : 'View receipt';
  }
  if (type === 'tournament_payment_incomplete') {
    const reason = String(notification?.reason || '').toLowerCase();
    if (reason === 'payment_failed') return 'Payment failed · Retry in Events';
    if (reason === 'checkout_expired' || reason === 'checkout_cancelled') {
      return 'Checkout ended · Register again if open';
    }
    return 'Complete payment in Events';
  }
  if (type === 'tournament_refund') return 'Collect refund from organizer';
  if (type === 'tournament_restored') return 'View tournament';
  if (type === 'tournament_bracket') {
    return 'Request to withdraw in My Registrations';
  }
  if (type === 'tournament_withdrawal') {
    const stage = String(notification?.stage || '').toLowerCase();
    if (stage === 'accepted') return 'Refund recorded · collect from organizer';
    if (stage === 'declined') return 'View registration';
    return 'View request in My Registrations';
  }
  if (type === 'tournament_transfer') {
    const stage = String(notification?.stage || '').toLowerCase();
    if (stage === 'accepted') return 'Open your new event';
    if (stage === 'declined') return 'View registration';
    return 'View request in My Registrations';
  }
  if (type === 'tournament_concluded') return 'Event concluded';
  return null;
}

/** Tailwind classes for the tournament notification CTA line. */
export function getTournamentNotificationCtaClass(notification) {
  const type = String(notification?.type || '').toLowerCase();
  if (type === 'tournament_payment_incomplete' || type === 'tournament_refund') {
    return 'text-amber-700 dark:text-amber-400';
  }
  if (type === 'tournament_withdrawal' && String(notification?.stage || '').toLowerCase() === 'accepted') {
    return 'text-amber-700 dark:text-amber-400';
  }
  if (type === 'tournament_transfer' && String(notification?.stage || '').toLowerCase() === 'declined') {
    return 'text-amber-700 dark:text-amber-400';
  }
  if (type === 'tournament_transfer') {
    return 'text-teal-700 dark:text-teal-400';
  }
  if (type === 'tournament_restored') return 'text-sky-700 dark:text-sky-400';
  return 'text-emerald-700 dark:text-emerald-400';
}
